import React, { useState } from 'react'
import Highlight, { defaultProps } from 'prism-react-renderer'
import lightTheme from 'prism-react-renderer/themes/nightOwlLight'
import darkTheme from 'prism-react-renderer/themes/github'
import { LiveProvider, LiveEditor, LiveError, LivePreview } from 'react-live'
import { mdx } from '@mdx-js/react'

import Box from './Box'
import Button from './Button'
import shortcodes from './shortcodes'
import useClipboard from './useClipboard'

export const liveEditorStyle = {
  fontSize: 14,
  overflowX: 'auto',
  fontFamily: 'Open Sans',
  borderRadius: 10,
}

export const liveErrorStyle = {
  fontFamily: 'monospace',
  fontSize: 14,
  padding: '1em',
  overflowX: 'auto',
  color: 'white',
  backgroundColor: '#e91e63',
  borderRadius: 10,
}

const LiveCodePreview = props => (
  <Box
    component={LivePreview}
    fontFamily="body"
    mt={5}
    p={3}
    css={{
      border: '1px solid rgb(226, 232, 240)',
      borderRadius: 4,
      marginBottom: 24,
    }}
    {...props}
  />
)

const CopyButton = props => (
  <Button
    size="sm"
    color="rose"
    style={{
      position: 'absolute',
      height: 24,
      top: 5,
      right: 10,
      zIndex: 1,
    }}
    {...props}
  />
)

const CodeBlock = ({
  className,
  live = 'false',
  isManual,
  children,
  ...props
}) => {
  const [editorCode, setEditorCode] = useState(children.trim())

  const language = className && className.replace(/language-/, '')
  const { onCopy, hasCopied } = useClipboard(editorCode)

  const themes = { light: lightTheme, dark: darkTheme }
  const theme = themes['dark']

  const liveProviderProps = {
    theme,
    language,
    code: editorCode,
    scope: {
      ...shortcodes,
      mdx,
    },
    noInline: isManual,
    ...props,
  }

  const handleCodeChange = newCode => setEditorCode(newCode.trim())

  if (['jsx', 'mdx'].includes(language) && live === 'true') {
    return (
      <LiveProvider {...liveProviderProps}>
        <LiveCodePreview />
        <Box css={{ position: 'relative' }}>
          <LiveEditor
            onChange={handleCodeChange}
            padding={20}
            style={liveEditorStyle}
          />
          <CopyButton onClick={onCopy}>
            {hasCopied ? 'copied' : 'copy'}
          </CopyButton>
        </Box>
        <LiveError style={liveErrorStyle} />
      </LiveProvider>
    )
  }

  return (
    <Highlight
      {...defaultProps}
      theme={theme}
      code={children}
      language={language}
    >
      {({ className, style, tokens, getLineProps, getTokenProps }) => (
        <pre className={className} style={{ ...style, padding: '13px 20px' }}>
          {tokens.map((line, i) => (
            <div key={i} {...getLineProps({ line, key: i })}>
              {line.map((token, key) => (
                <span key={key} {...getTokenProps({ token, key })} />
              ))}
            </div>
          ))}
        </pre>
      )}
    </Highlight>
  )
}

export default CodeBlock
